import { Package } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { Product } from '@/types/products';

/**
 * Botón «Vincular con Dropi» (docs/DROPI.md): la única acción que cambia el vínculo del producto.
 *
 * Tiene IDENTIDAD PROPIA (no un camión genérico) para que se distinga de un vistazo: icono de caja
 * de `lucide-react` con el naranja de Dropi y, cuando ya hay vínculo, el «check» verde de
 * confirmado. La etiqueta dice SIEMPRE el objetivo (qué producto de Dropi quedó vinculado y con qué
 * proveedor), porque es el dato que el admin necesita para no vincular dos veces.
 *
 * Abre `ProductDropiDialog` (buscador del catálogo + vínculo por id) y por eso recibe `onVincular`
 * ya resuelto: este botón no sabe de diálogos, solo de cómo se ve el vínculo.
 */
export interface DropiLinkButtonProps {
  product: Product;
  onVincular: () => void;
}

export function DropiLinkButton({ product, onVincular }: DropiLinkButtonProps) {
  const vinculado = Boolean(product.dropiProductId);
  const ciudad = product.dropiBodega?.ciudad ?? null;

  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={`Vincular ${product.nombre} con Dropi`}
      title={
        vinculado
          ? [
              `Vinculado con Dropi #${product.dropiProductId}`,
              product.dropiProveedor,
              ciudad ? `sale de ${ciudad}` : null,
              'clic para cambiarlo o desvincularlo',
            ]
              .filter(Boolean)
              .join(' · ')
          : 'Vincular con Dropi: pegá el id del producto o buscalo en el catálogo'
      }
      onClick={onVincular}
      className="relative"
    >
      <Package size={16} className="text-warning-strong" aria-hidden="true" />
      {vinculado ? (
        <span
          aria-hidden="true"
          className="absolute right-1.5 bottom-1.5 grid size-3 place-items-center rounded-pill bg-success text-surface"
        >
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
            <path
              d="M1.5 4.25L3.25 6L6.5 2.25"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ) : null}
    </Button>
  );
}
